/**
 * Insert `<br />` tags before newlines, like PHP's `nl2br()` with default parameters.
 *
 * @param str Input text
 * @param replaceMode Use replace instead of insert
 * @param isXhtml Use XHTML
 */
export function nl2br(str: unknown, replaceMode = false, isXhtml = true): string {
  const breakTag = isXhtml ? '<br />' : '<br>';
  const replaceStr = replaceMode ? `$1${breakTag}` : `$1${breakTag}$2`;
  return `${str}`.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, replaceStr);
}

function toAlphaNumeric(value: string, char = 'A-Za-z0-9 ', toSpace = ['-', '_']): string {
  const mapping = {
    ä: 'ae',
    ü: 'ue',
    ö: 'oe',
    Ä: 'Ae',
    Ö: 'Oe',
    Ü: 'Ue',
  };

  let result = value;
  Object.entries(mapping).forEach(([letter, replacement]) => {
    result = result.replaceAll(letter, replacement);
  });

  toSpace.forEach((letter) => {
    result = result.replaceAll(letter, ' ');
  });

  if (result.includes(' ')) {
    result = result.toLowerCase();
  }

  // Known issue (kept to preserve behaviour): this replaces the literal string, not a regex
  // character class, so non-alphanumeric characters are not removed.
  return result.replace(`/[^${char}]/`, '');
}

function ucwords(str: string): string {
  return `${str}`.replace(/^([a-z])|\s+([a-z])/g, (match) => match.toUpperCase());
}

function lowerCaseFirstLetter(val: string): string {
  return String(val).charAt(0).toLowerCase() + String(val).slice(1);
}

/**
 * Transliterate umlauts, drop separators and return the value in camel case.
 */
export function toAlphaNumericCamelCase(value: string): string {
  // Known issue (kept to preserve behaviour): the former camel-case check compared
  // `RegExp#test` with `0` and therefore never lowercased the value; it is omitted here.
  let result = toAlphaNumeric(value);
  result = ucwords(result);
  result = lowerCaseFirstLetter(result);

  return result.replaceAll(' ', '');
}

export const toKebabCase = (str: string): string => {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};

export const formatStringForDisplay = (str = ''): string => {
  let s = String(str);
  s = s.replaceAll('__', '/');
  s = s.replaceAll('_', ' ');
  return s;
};

export const slugify = (title: string | undefined) => {
  if (!title) return '';
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
};

/** Escape `&`, `"`, `'`, `<` and `>` for use inside a double-quoted HTML attribute. */
export function escapeHtmlAttribute(value: string): string {
  if (!value) return '';

  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Trimmed, lower-cased string for case-insensitive comparisons of META and config values. */
export function normalizeForComparison(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}
